import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import {
  calendar,
  clientReservations,
  instructorReservations,
  type ClientReservationPayload,
  type InstructorReservationPayload,
  type Occurrence,
} from '@/api/endpoints'
import type { CalendarResponse } from '@/types/api'

export const calendarKey = (start: string, days: number, centers: number[] = []) =>
  ['calendar', start, days, centers.join(',')] as const

export function useCalendar(start: string, days: number, centers: number[] = []) {
  return useQuery<CalendarResponse>({
    queryKey: calendarKey(start, days, centers),
    queryFn: () => calendar.get(start, days, centers),
    placeholderData: (previous) => previous,
  })
}

/** Every mutation invalidates the whole calendar - fees and rewards change too.
 *  `changelog` is here too: every one of these writes a new audit row, and
 *  the Undo dropdown (`UndoRedoControl`) reads that same cache - without
 *  this it would keep showing whatever was in it before the action. */
function useCalendarInvalidation() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    void qc.invalidateQueries({ queryKey: ['fees'] })
    void qc.invalidateQueries({ queryKey: ['rewards'] })
    void qc.invalidateQueries({ queryKey: ['changelog'] })
  }
}

/** Ako často sa pýtať, či sa niečo zmenilo. */
const SYNC_MS = 15_000

/**
 * Prekreslenie kalendára, keď zmenu urobil niekto iný.
 *
 * Dvaja ľudia na recepcii, alebo ten istý človek v druhom okne. Poľuje sa
 * **token**, nie kalendár: odpoveď má tridsať bajtov a celý kalendár sa
 * stiahne, len keď sa token naozaj zmení.
 *
 * Beží to na poľovaní a nie na push spojení zámerne — dôvod je v
 * `CalendarVersionView`: produkcia má synchrónne gunicorn workery a jedno
 * dlhodobo otvorené spojenie by jeden z nich obsadilo nastálo.
 *
 * V pozadí okna sa nepoľuje. `refetchInterval` má predvolene vypnuté
 * `refetchIntervalInBackground`, takže odložená záložka server nezaťažuje —
 * a keď sa do nej človek vráti, `refetchOnWindowFocus` dáta natiahne aj tak.
 */
export function useCalendarSync() {
  const invalidate = useCalendarInvalidation()
  const { data: version } = useQuery({
    queryKey: ['calendar-version'],
    queryFn: calendar.version,
    refetchInterval: SYNC_MS,
  })

  // Prvá videná hodnota je východisko, nie zmena - inak by sa kalendár
  // zbytočne natiahol druhýkrát hneď po otvorení stránky.
  const seen = useRef<string | null>(null)

  useEffect(() => {
    if (version === undefined) return
    if (seen.current === null) {
      seen.current = version
      return
    }
    if (seen.current !== version) {
      seen.current = version
      invalidate()
    }
    // `invalidate` sa vytvára pri každom rendri, do závislostí preto nepatrí -
    // efekt má reagovať na zmenu tokenu, nie na prekreslenie stránky.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])
}

export function useSaveInstructorReservation() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (payload: InstructorReservationPayload) =>
      payload.id
        ? instructorReservations.update(payload.id, payload)
        : instructorReservations.create(payload),
    onSuccess: invalidate,
  })
}

/**
 * Viac termínov naraz — jeden inštruktor, niekoľko dní (napr. sústredenie).
 *
 * Vlastná mutácia, nie slučka nad `useSaveInstructorReservation` — rovnaký
 * dôvod ako pri `useSaveClientReservations`: server to založí v jednej
 * transakcii a inštruktora upsertne raz.
 */
export function useSaveInstructorReservations() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (payload: InstructorReservationPayload & { occurrences: Occurrence[] }) =>
      instructorReservations.createMany(payload),
    onSuccess: invalidate,
  })
}

export function useDeleteInstructorReservation() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (id: number) => instructorReservations.remove(id),
    onSuccess: invalidate,
  })
}

export function useSetInstructorReservationStatus() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({ id, statusId }: { id: number; statusId: number }) =>
      instructorReservations.setStatus(id, statusId),
    onSuccess: invalidate,
  })
}

export function useFillFavorites() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({ day, days }: { day: string; days: number }) =>
      instructorReservations.createForFavorites(day, days),
    onSuccess: invalidate,
  })
}

/**
 * Viac termínov naraz — kurz rozložený do niekoľkých dní.
 *
 * Vlastná mutácia, nie slučka nad `useSaveClientReservation`: server to robí
 * v jednej transakcii a klienta zakladá raz. Slučka na frontende by pri
 * chybe v strede zanechala polovicu kurzu a operátor by nevedel, kde skončil.
 */
export function useSaveClientReservations() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (payload: ClientReservationPayload & { occurrences: Occurrence[] }) =>
      clientReservations.createMany(payload),
    onSuccess: invalidate,
  })
}

export function useSaveClientReservation() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (payload: ClientReservationPayload) =>
      payload.id
        ? clientReservations.update(payload.id, payload)
        : clientReservations.create(payload),
    onSuccess: invalidate,
  })
}

export function useDeleteClientReservation() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (id: number) => clientReservations.remove(id),
    onSuccess: invalidate,
  })
}

export function useSetClientReservationStatus() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({ id, statusId }: { id: number; statusId: number }) =>
      clientReservations.setStatus(id, statusId),
    onSuccess: invalidate,
  })
}

export function useSetClientReservationPayment() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({ id, clientPaid }: { id: number; clientPaid: number }) =>
      clientReservations.setPayment(id, clientPaid),
    onSuccess: invalidate,
  })
}

export function useMoveClientReservation() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number
      day: string
      part: number
      instructor_reservation_id?: number | null
      copy?: boolean
      confirm_paid?: boolean
    }) => clientReservations.move(id, body),
    onSuccess: invalidate,
  })
}
